// ManageMediaCarousel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BlockBlobClient } from "@azure/storage-blob";
import apiClient from "../../../apiClient";
import { getTsDate, notify } from "../../../utils";
import useUser from "../../../customHooks/useUser";
import { FormGroup } from "reactstrap";
import { Label } from "reactstrap";
import { Input } from "reactstrap";
import { ButtonGroup } from "reactstrap";
import { Button } from "reactstrap";

// ---------- Types ----------
type ItemStatus = "queued" | "uploading" | "paused" | "completed" | "error" | "canceled";
type MediaKind = "image" | "video";

type QueueItem = {
    id: string;
    file: File;
    mediaKind: MediaKind;
    container: string;       // "images" | "videos" (derived)
    blobName?: string;
    sasUrl?: string;
    progress: number;        // 0..100
    status: ItemStatus;
    error?: string | null;
};

type UploadCompleteInfo = {
    container: string;
    blobName: string;
    fileName: string;
    size: number;
    mime: string;
    url: string; // permanent (no SAS)
};

// ---------- Config ----------
const BLOCK_SIZE = 8 * 1024 * 1024; // 8 MB
const MAX_RETRIES = 4;
const CONCURRENCY = 1; // ⟵ single-upload at a time

// ---------- Helpers ----------
function pad(n: number, width: number) {
    const s = n.toString();
    return s.length >= width ? s : "0".repeat(width - s.length) + s;
}

function uploadKey(sasUrl: string, size: number, blockSize: number) {
    const url = new URL(sasUrl);
    const path = `${url.host}${url.pathname}`;
    return `${path}:${size}:${blockSize}`;
}

type Persisted = { committed?: boolean; nextIndex: number };
function readPersisted(key: string): Persisted | null {
    try { const raw = localStorage.getItem(`upload:${key}`); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writePersisted(key: string, val: Persisted) {
    try { localStorage.setItem(`upload:${key}`, JSON.stringify(val)); } catch { }
}
function clearPersisted(key: string) {
    try { localStorage.removeItem(`upload:${key}`); } catch { }
}

const isImage = (f: File) => /^image\/(png|jpe?g|webp)$/i.test(f.type);
const isVideo = (f: File) => f.type === "video/mp4";
const pickMediaKind = (f: File): MediaKind => (isImage(f) ? "image" : "video");
const pickContainer = (kind: MediaKind) => (kind === "image" ? "images" : "videos");

// ---------- Upload core (manual blocks with resume) ----------
async function stageAllBlocksWithResume(
    file: File,
    sasUrl: string,
    opts: {
        blockSize?: number;
        onProgress?: (pct: number) => void;
        abortSignal?: AbortSignal;
    }
) {
    const blockSize = opts.blockSize ?? BLOCK_SIZE;
    const client = new BlockBlobClient(sasUrl);
    const size = file.size;
    const totalBlocks = Math.ceil(size / blockSize);
    const idWidth = totalBlocks.toString().length;

    const key = uploadKey(sasUrl, size, blockSize);
    const persisted = readPersisted(key) ?? { nextIndex: 0 };
    let nextIndex = persisted.nextIndex;

    let uploadedBytes = nextIndex * blockSize;
    opts.onProgress?.(Math.round((uploadedBytes / size) * 100));

    const stage = async (index: number, attempt = 0): Promise<void> => {
        const start = index * blockSize;
        const end = Math.min(start + blockSize, size);
        const slice = file.slice(start, end);
        const arrayBuffer = await slice.arrayBuffer();
        const blockId = btoa(pad(index, idWidth));

        try {
            await client.stageBlock(blockId, arrayBuffer, arrayBuffer.byteLength, {
                abortSignal: opts.abortSignal,
            });
        } catch (e) {
            if (opts.abortSignal?.aborted) throw e;
            if (attempt < MAX_RETRIES) {
                const delay = 2 ** attempt * 500;
                await new Promise((r) => setTimeout(r, delay));
                return stage(index, attempt + 1);
            }
            throw e;
        }
        uploadedBytes = Math.min(size, uploadedBytes + (end - start));
        opts.onProgress?.(Math.round((uploadedBytes / size) * 100));
    };

    for (let i = nextIndex; i < totalBlocks; i++) {
        if (opts.abortSignal?.aborted) throw new DOMException("Aborted", "AbortError");
        await stage(i);
        nextIndex = i + 1;
        writePersisted(key, { nextIndex });
    }

    const blockList = Array.from({ length: totalBlocks }, (_, i) => btoa(pad(i, idWidth)));
    await client.commitBlockList(blockList, {
        abortSignal: opts.abortSignal,
        blobHTTPHeaders: { blobContentType: file.type || "application/octet-stream" },
    });

    writePersisted(key, { nextIndex: totalBlocks, committed: true });
    clearPersisted(key);
}

// ---------- UI Component ----------
export default function UploadManager({
    mutate
}: {
    mutate: () => void;
}) {
    const { user } = useUser();
    const [items, setItems] = useState<QueueItem[]>([]);
    const uploads = useRef<Map<string, AbortController>>(new Map());
    const activeCount = useMemo(() => items.filter((i) => i.status === "uploading").length, [items]);
    const [s_description, set_s_description] = useState('');
    const [unit, setUnit] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setUnit(user.s_unit);
    }, [user.s_unit]);

    // Request SAS (pass container explicitly, since we choose it client-side)
    const requestSas = useCallback(async (file: File, container: string) => {
        try {
            const res = await apiClient.post("/sas", {
                container,
                fileName: file.name,
                contentType: file.type,
            });
            return res.data as { blobUrl: string; blobName: string };
        } catch (err) {
            throw new Error(`SAS request failed: ${err}`);
        }
    }, []);

    // Add one file (single upload at a time)
    const onPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // validate type
        if (!isImage(file) && !isVideo(file)) {
            notify("Please select a PNG/JPG/WEBP image or an MP4 video.");
            e.currentTarget.value = "";
            return;
        }

        // optional: block if something is already in progress
        const busy = items.some((i) => ["queued", "uploading", "paused"].includes(i.status));
        if (busy) {
            notify("Please wait for the current upload to finish (single upload mode).");
            e.currentTarget.value = "";
            return;
        }

        const kind = pickMediaKind(file);
        const container = pickContainer(kind);

        const newItem: QueueItem = {
            id: crypto.randomUUID(),
            file,
            mediaKind: kind,
            container,
            progress: 0,
            status: "queued",
        };
        setItems((prev) => [...prev, newItem]);
        e.currentTarget.value = ""; // allow selecting same file later
    }, [items]);

    // Scheduler: with CONCURRENCY = 1 this runs one-at-a-time
    useEffect(() => {
        const canStart = () => items.some((i) => i.status === "queued" || i.status === "paused");
        if (!canStart()) return;

        const startMore = async () => {
            let slots = Math.max(0, CONCURRENCY - activeCount);
            if (!slots) return;

            const toStart = items
                .filter((i) => i.status === "queued" || i.status === "paused")
                .slice(0, slots);

            for (const item of toStart) {
                setItems((prev) =>
                    prev.map((x) => (x.id === item.id ? { ...x, status: "uploading", error: null } : x))
                );

                (async () => {
                    try {
                        // SAS for chosen container + final filename
                        let sasUrl = item.sasUrl;
                        let blobName = item.blobName;
                        if (!sasUrl) {
                            const { blobUrl, blobName: name } = await requestSas(item.file, item.container);
                            sasUrl = blobUrl; blobName = name;
                            setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, sasUrl, blobName } : x)));
                        }

                        // Prepare abort
                        const ctrl = new AbortController();
                        uploads.current.set(item.id, ctrl);

                        // Upload (resumable for both image & video)
                        await stageAllBlocksWithResume(item.file, sasUrl!, {
                            blockSize: BLOCK_SIZE,
                            abortSignal: ctrl.signal,
                            onProgress: (pct) => {
                                setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, progress: pct } : x)));
                            },
                        });

                        uploads.current.delete(item.id);

                        // Build clean URL for DB
                        const u = new URL(sasUrl!);
                        const urlWithoutQuery = `https://${u.host}${u.pathname}`;
                        const completeInfo: UploadCompleteInfo = {
                            container: item.container,
                            blobName: item.blobName!,
                            fileName: item.file.name,
                            size: item.file.size,
                            mime: item.file.type || "application/octet-stream",
                            url: urlWithoutQuery,
                        };

                        // Save record (include media type)
                        await apiClient.post("/save-media", {
                            s_file_name: blobName,
                            s_description,
                            s_file_type: item.mediaKind, // <-- "image" or "video"
                            s_created_by: user.s_email,
                            t_created: getTsDate(),
                            s_unit: unit
                            // s_file_link: urlWithoutQuery,
                        });

                        setItems((prev) =>
                            prev.map((x) => (x.id === item.id ? { ...x, status: "completed", progress: 100 } : x))
                        );

                        setLoading(true);
                        mutate();
                        // Auto clear finished card after a moment
                        setTimeout(() => {
                            clearDone();
                            set_s_description('');
                            setLoading(false);
                        }, 3000);
                        notify(`${item.mediaKind === "image" ? "Image" : "Video"} uploaded successfully`);
                    } catch (err: any) {
                        const aborted = err?.name === "AbortError";
                        uploads.current.delete(item.id);
                        setItems((prev) =>
                            prev.map((x) =>
                                x.id === item.id
                                    ? { ...x, status: aborted ? "paused" : "error", error: aborted ? null : String(err?.message || err) }
                                    : x
                            )
                        );
                    }
                })();

                slots--;
                if (slots <= 0) break;
            }
        };

        startMore();
    }, [items, activeCount, requestSas]);

    // Controls
    const pause = useCallback((id: string) => {
        uploads.current.get(id)?.abort();
        uploads.current.delete(id);
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: "paused" } : x)));
    }, []);

    const resume = useCallback((id: string) => {
        setItems((prev) =>
            prev.map((x) => (x.id === id && (x.status === "paused" || x.status === "error") ? { ...x, status: "queued" } : x))
        );
    }, []);

    const cancel = useCallback((id: string) => {
        uploads.current.get(id)?.abort();
        uploads.current.delete(id);
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: "canceled" } : x)));
    }, []);

    const clearDone = useCallback(() => {
        setItems((prev) => prev.filter((x) => !["completed", "canceled"].includes(x.status)));
    }, []);

    const busy = items.some((i) => ["queued", "uploading", "paused"].includes(i.status));

    return (
        <div>
            <FormGroup>
                <Label className={'mr-2'}>Unit</Label>
                <ButtonGroup>
                    {
                        [user.s_unit, '*'].map((item, i) => (
                            <Button 
                                key={i} 
                                onClick={() => setUnit(item)}
                                active={item === unit}
                            >
                                {item === '*' ? 'GLOBAL' : item}
                            </Button>
                        ))
                    }
                </ButtonGroup>
            </FormGroup>
            <FormGroup>
                <Label>Name</Label>
                <Input value={s_description} onChange={e => set_s_description(e.target.value)} />
            </FormGroup>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }} className={`${!s_description || s_description.length < 5 || loading ? 'custom-disabled' : ''}`}>
                <input
                    type="file"
                    accept="image/png,image/jpeg,video/mp4"
                    onChange={onPick}
                    disabled={busy} // single-upload mode UX
                />
            </div>

            <div>
                {items.map((it) => (
                    <div key={it.id} style={{
                        border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 10, background: "#fff"
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <div style={{ fontWeight: 600 }}>
                                [{it.mediaKind.toUpperCase()}] {it.file.name}
                                <span style={{ opacity: 0.6, fontWeight: 400 }}> ({(it.file.size / 1024 / 1024).toFixed(1)} MB)</span>
                            </div>
                            <div>
                                <span style={{ textTransform: "capitalize" }}>{it.status}</span>
                                {it.error && <span style={{ color: "#b91c1c" }}> • {it.error}</span>}
                            </div>
                        </div>

                        <div style={{ height: 10, background: "#eef2ff", borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
                            <div style={{
                                height: "100%",
                                width: `${it.progress}%`,
                                background: "#6366f1",
                                transition: "width .2s ease"
                            }} />
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                            {it.status === "uploading" && <button onClick={() => pause(it.id)}>Pause</button>}
                            {(it.status === "paused" || it.status === "error") && <button onClick={() => resume(it.id)}>Resume</button>}
                            {!(it.status === "completed" || it.status === "canceled") && <button onClick={() => cancel(it.id)}>Cancel</button>}
                            {it.blobName && it.status === "completed" && (
                                <a
                                    href={`https://${new URL(it.sasUrl!).host}${new URL(it.sasUrl!).pathname}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ marginLeft: "auto" }}
                                >
                                    View in Blob
                                </a>
                            )}
                        </div>
                    </div>
                ))}
                {!items.length && <div style={{ opacity: 0.65 }}>Pick an image or an MP4 to begin (single upload mode).</div>}
            </div>
        </div>
    );
}
